'use client';

/**
 * UrlDocumentViewer / FileViewer.
 *
 * The shared file-preview primitive used wherever we render a tenant
 * file from a presigned URL — record attachments, training-evidence
 * uploads, and any other non-vault file source. Sister of
 * ``ReadOnlyDocumentViewer`` (which fetches a vault document by
 * documentId + revisionId); the two cover the two ways files reach
 * the UI in this app.
 *
 * Both components mount the same underlying ``DocumentViewer``
 * (PDF.js + image / Word renderers) so all callers share a single
 * rendering surface; the only difference is how the file content is
 * resolved.
 *
 * Re-exported as ``FileViewer`` from ``index.ts`` — prefer that name
 * for new consumers since "Document" in the legacy name predates
 * generic file previewing.
 *
 * Uses the contentType prop for file type detection instead of
 * downloading the blob just to read its MIME type.
 */

import React, {Suspense, lazy} from 'react';
import LoadingSpinner from '@/matic/components/ui/LoadingSpinner';

const DocumentViewer = lazy(async () => {
  const module =
    await import('@/matic/features/vault/documents/components/DocumentViewer/DocumentViewer');
  return {default: module.DocumentViewer};
});

export type UrlDocumentViewerProps = {
  /** Presigned URL for the document */
  url?: string;
  /** MIME content type (e.g., "application/pdf") for file type detection */
  contentType?: string;
  /** Whether the URL is still being fetched */
  loading?: boolean;
  /** Error message */
  error?: string;
  /** Header title (default: "Document Preview") */
  title?: string;
  /** Message shown when no URL is provided */
  emptyMessage?: string;
  /** Additional CSS classes */
  className?: string;
  /** Page to scroll to on initial render (1-indexed) */
  initialPage?: number;
  /** Increment to re-trigger scroll-to-page on repeat clicks of the same citation */
  scrollKey?: number;
  /** Fired once the underlying viewer has painted its first page. Forwarded
   *  to DocumentViewer so callers can hold a single outer loading state
   *  across the full mount → parse → paint chain. */
  onReady?: () => void;
};

export function UrlDocumentViewer({
  url,
  contentType,
  loading = false,
  error,
  title,
  emptyMessage = 'No document to display',
  className,
  initialPage,
  scrollKey,
  onReady,
}: UrlDocumentViewerProps) {
  if (error) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600">Error loading document: {error}</p>
        </div>
      </div>
    );
  }

  if (!url && !loading) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center bg-gray-50">
          <LoadingSpinner />
        </div>
      }
    >
      <DocumentViewer
        documentSource={url}
        contentType={contentType}
        loading={loading || !url}
        disabled={true}
        scrollMode={true}
        title={title}
        className={className}
        initialPage={initialPage}
        scrollKey={scrollKey}
        onReady={onReady}
      />
    </Suspense>
  );
}
